import { addDoc, collection, serverTimestamp } from 'firebase/firestore'
import { firebaseAuth, firestore } from '../firebase'

interface PendingReport {
  message: string
  stack?: string
  scope: string
  url: string
  capturedAt: number
  context?: Record<string, unknown>
}

const MAX_BUFFER = 100
const buffer: PendingReport[] = []
let flushing = false
let initialized = false

const scrubStack = (stack?: string) => {
  if (!stack) return undefined
  return stack
    .replace(/([A-Za-z]:\\Users\\)[^\\\/]+/g, '$1<USER>')
    .replace(/(\/Users\/)[^\/]+/g, '$1<USER>')
    .replace(/(\/home\/)[^\/]+/g, '$1<USER>')
}

const flush = async () => {
  if (flushing) return
  if (buffer.length === 0) return
  const user = firebaseAuth.currentUser
  if (!user) return

  flushing = true
  try {
    const colRef = collection(firestore, `users/${user.uid}/errorReports`)
    const drained = buffer.splice(0, buffer.length)
    for (let i = 0; i < drained.length; i++) {
      const r = drained[i]
      try {
        await addDoc(colRef, {
          ...r,
          source: 'pwa',
          userAgent: navigator.userAgent,
          createdAt: serverTimestamp(),
        })
      } catch (writeErr) {
        // Re-queue this one and the rest, stop until next trigger.
        buffer.unshift(...drained.slice(i))
        console.warn('[errorReporter] Flush write failed, re-queued:', writeErr)
        break
      }
    }
  } finally {
    flushing = false
  }
}

const enqueue = (
  scope: string,
  message: string,
  stack?: string,
  context?: Record<string, unknown>
) => {
  if (buffer.length >= MAX_BUFFER) buffer.shift()
  buffer.push({
    message,
    stack: scrubStack(stack),
    scope,
    url: window.location.href,
    capturedAt: Date.now(),
    context,
  })
  flush()
}

/**
 * Wires window-level error capture and flushes anything buffered while
 * signed out the moment the auth state flips to a real user. Called once
 * from main.tsx; safe to call again — second call is a no-op.
 */
export function initErrorReporter() {
  if (initialized) return
  initialized = true

  window.addEventListener('error', (event) => {
    const err = event.error
    const message =
      (err && typeof err === 'object' && 'message' in err && String((err as Error).message)) ||
      event.message ||
      'window.error'
    const stack =
      err && typeof err === 'object' && 'stack' in err ? String((err as Error).stack) : undefined
    enqueue('pwa:window.error', message, stack, {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    })
  })

  window.addEventListener('unhandledrejection', (event) => {
    const reason = (event as PromiseRejectionEvent).reason
    if (reason instanceof Error) {
      enqueue('pwa:unhandledRejection', reason.message, reason.stack)
    } else {
      enqueue(
        'pwa:unhandledRejection',
        typeof reason === 'string' ? reason : safeStringify(reason)
      )
    }
  })

  // Flush whenever auth flips, so reports captured pre-sign-in actually leave.
  firebaseAuth.onIdTokenChanged((u) => {
    if (u) {
      // One tick of slack so any sign-in side-effects settle first.
      setTimeout(() => flush(), 0)
    }
  })
}

export function reportPwaError(
  error: unknown,
  scope: string,
  context?: Record<string, unknown>
) {
  if (error instanceof Error) {
    enqueue(scope, error.message, error.stack, context)
  } else {
    enqueue(scope, typeof error === 'string' ? error : safeStringify(error), undefined, context)
  }
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}
